import React from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import { Filter, X } from 'lucide-react';
import { Button } from '../ui/button';

interface AlertFilterSidebarProps {
  children: React.ReactNode;
  reset: () => void;
  applyFilters: () => void;
  isLoading?: boolean;
}

const AlertFilterSidebar: React.FC<AlertFilterSidebarProps> = ({
  children,
  reset,
  applyFilters,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleApplyFilters = () => {
    applyFilters();
    setIsOpen(false);
  };

  const handleReset = () => {
    reset();
    setIsOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        className="flex items-center gap-2 h-10 px-4 border-gray-300 hover:border-primary hover:text-primary transition-colors"
        onClick={() => setIsOpen(true)}
      >
        <Filter className="h-4 w-4" />
        Filters
      </Button>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 540, md: 400 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Filter className="h-5 w-5" style={{ color: 'var(--primary-color, #3b82f6)' }} />
            <Typography variant="h6" fontWeight={600}>
              Filter Alerts
            </Typography>
          </Box>
          <IconButton
            onClick={() => setIsOpen(false)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <X className="h-5 w-5" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            p: 3,
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="default"
              onClick={handleApplyFilters}
              disabled={isLoading}
              className="flex-1 h-11 bg-primary hover:bg-primary-lighter text-white font-medium"
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={isLoading}
              className="flex-1 h-11 flex items-center justify-center gap-2 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              Clear All
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AlertFilterSidebar;